// "How much do you need?" helper drawer for the PDP box-mode sqft calculator.
//
// The shopper enters one or more room sections as width × length (Ft + in). The
// element sums them into a measured area (ceil'd to whole sqft), applies overage,
// and on "Update Quantity" writes the raw, pre-overage Subtotal into the PDP
// Square Feet input and copies its overage into the PDP overage <select>, then
// closes the drawer. The existing _onSqftInput() cascade in sqft-calculator.js
// re-derives the box count — see
// docs/adr/adr-20260604-01-sqft-helper-drawer-writeback-contract.md.
//
// Box-count parity: this reuses sqftToBoxes from sqft-calculator.js (mapped via
// the theme.liquid importmap / vitest alias) so "Total boxes needed" matches what
// the PDP renders after the write-back. Zero edits are made to sqft-calculator.js.
import { sqftToBoxes, formatSqft } from 'sqft-calculator';

// A foot value from a Ft + in pair. Blank parses to 0; negatives are floored to 0
// (the inputs also set min="0"). Inches are deliberately lenient — no 0–11 clamp —
// so "18 in" is accepted as 1.5 ft.
export function feetFromParts(ft, inch) {
    const f = Math.max(0, parseFloat(ft) || 0);
    const i = Math.max(0, parseFloat(inch) || 0);
    return f + i / 12;
}

// Area of one section in sqft. A section contributes only when both dimensions are
// positive — a partial row (one dimension blank/zero) silently contributes 0.
export function sectionArea({ wFt, wIn, lFt, lIn }) {
    const width = feetFromParts(wFt, wIn);
    const length = feetFromParts(lFt, lIn);
    if (width <= 0 || length <= 0) return 0;
    return width * length;
}

// Measured Subtotal: sum of section areas, ceil'd to whole sqft (never under-order).
export function measureSqft(sections) {
    const total = sections.reduce((sum, s) => sum + sectionArea(s), 0);
    return Math.ceil(total);
}

class SqftHelper extends HTMLElement {
    connectedCallback() {
        // x-drawer (shouldAppendToBody) relocates the drawer — and this nested
        // element — to <body> on open, which fires connectedCallback again on the
        // same instance. Init once so reopening doesn't append a duplicate row or
        // double-bind listeners; the existing rows move with the subtree and persist.
        if (this._initialized) return;
        this._initialized = true;

        this._sqftPerBox = parseFloat(this.getAttribute('data-sqft-per-box')) || 1;
        this._sqftTargetId = this.getAttribute('data-sqft-target');
        this._overageTargetId = this.getAttribute('data-overage-target');

        this._sectionsEl = this.querySelector('[data-sections]');
        this._rowTemplate = this.querySelector('[data-row-template]');
        this._addButton = this.querySelector('[data-add-section]');
        this._overageSelect = this.querySelector('[data-overage-select]');

        this._summarySubtotal = this.querySelector('[data-summary-subtotal]');
        this._summaryOverage = this.querySelector('[data-summary-overage]');
        this._summaryTotal = this.querySelector('[data-summary-total]');
        this._summaryBoxes = this.querySelector('[data-summary-boxes]');
        this._summarySqftIncluded = this.querySelector('[data-summary-sqft-included]');

        // x-drawer relocates to <body> on open, but this whole subtree moves with
        // it, so closest() stays valid. The update button lives in the drawer's
        // footer slot (a sibling of this element), reached via the drawer.
        this._drawer = this.closest('x-drawer');
        this._updateButton = this._drawer ? this._drawer.querySelector('[data-update]') : null;

        // Start with a single blank section.
        this._addSection();

        if (this._sectionsEl) {
            this._sectionsEl.addEventListener('input', () => this._render());
            this._sectionsEl.addEventListener('click', (event) => {
                const remove = event.target.closest('[data-remove-row]');
                if (remove) this._removeRow(remove.closest('[data-row]'));
            });
        }
        if (this._addButton) {
            this._addButton.addEventListener('click', () => this._addSection());
        }
        if (this._overageSelect) {
            this._overageSelect.addEventListener('change', () => this._render());
        }
        if (this._updateButton) {
            this._updateButton.addEventListener('click', () => this._onUpdate());
        }
        // Re-sync overage from the PDP each time the drawer opens, so the two never
        // start out of step (see adr-20260604-01 — overage lives in two places by design).
        if (this._drawer) {
            this._drawer.addEventListener('toggle', () => {
                if (this._drawer.hasAttribute('open')) this._syncOverageFromPdp();
            });
        }

        this._syncOverageFromPdp();
        this._render();
    }

    _rows() {
        return this._sectionsEl ? Array.from(this._sectionsEl.querySelectorAll('[data-row]')) : [];
    }

    _readSections() {
        return this._rows().map((row) => ({
            wFt: row.querySelector('[data-w-ft]')?.value,
            wIn: row.querySelector('[data-w-in]')?.value,
            lFt: row.querySelector('[data-l-ft]')?.value,
            lIn: row.querySelector('[data-l-in]')?.value,
        }));
    }

    _overagePct() {
        return parseInt(this._overageSelect ? this._overageSelect.value : '', 10) || 0;
    }

    _addSection() {
        if (!this._sectionsEl || !this._rowTemplate) return;
        const fragment = this._rowTemplate.content.cloneNode(true);
        this._sectionsEl.appendChild(fragment);
        const added = this._sectionsEl.lastElementChild;
        this._refreshRowControls();
        // Auto-focus the new row's first field on desktop only. On touch devices,
        // forcing focus pops the soft keyboard, which shrinks the visual viewport
        // while this bottom-sheet drawer stays sized against the layout viewport
        // (max-height: 75vh) — so it appears not to return to its original size —
        // and the focus-driven scroll-into-view defeats the body scroll-lock
        // (overflow:hidden), leaving the page shifted after the drawer closes. This
        // fires on open (the initial blank row) and on every "Add Another Area".
        // Mirrors the drawer focus-trap heuristic in theme.js (initial focus only
        // when pointer: fine).
        const firstInput = added && added.querySelector('input');
        if (firstInput && window.matchMedia?.('(pointer: fine)')?.matches) {
            firstInput.focus();
        }
        this._render();
    }

    _removeRow(row) {
        if (!row) return;
        // The first section is never removable; guard against removing the last row.
        if (this._rows().length <= 1) return;
        row.remove();
        this._refreshRowControls();
        this._render();
    }

    // The remove control only appears on rows 2+ — the first section can't be removed.
    _refreshRowControls() {
        const rows = this._rows();
        rows.forEach((row, index) => {
            const remove = row.querySelector('[data-remove-row]');
            if (remove) remove.hidden = index === 0;
        });
    }

    _syncOverageFromPdp() {
        if (!this._overageSelect || !this._overageTargetId) return;
        const pdpOverage = document.getElementById(this._overageTargetId);
        if (!pdpOverage) return;
        const match = Array.from(this._overageSelect.options).some(
            (opt) => opt.value === pdpOverage.value,
        );
        if (match) {
            this._overageSelect.value = pdpOverage.value;
            this._render();
        }
    }

    _render() {
        const subtotal = measureSqft(this._readSections());
        const overagePct = this._overagePct();
        const overageSqft = Math.ceil(subtotal * (overagePct / 100));
        const totalSqft = Math.ceil(subtotal * (1 + overagePct / 100));
        const boxes = subtotal > 0 ? sqftToBoxes(subtotal, overagePct, this._sqftPerBox) : 0;
        const sqftIncluded = boxes * this._sqftPerBox;
        const boxLabel = boxes === 1 ? 'box' : 'boxes';

        if (this._summarySubtotal) {
            this._summarySubtotal.textContent = `${formatSqft(subtotal)} sqft`;
        }
        if (this._summaryOverage) {
            this._summaryOverage.textContent = `${formatSqft(overageSqft)} sqft`;
        }
        if (this._summaryTotal) {
            this._summaryTotal.textContent = `${formatSqft(totalSqft)} sqft`;
        }
        if (this._summaryBoxes) {
            this._summaryBoxes.textContent = `${boxes} ${boxLabel}`;
        }
        if (this._summarySqftIncluded) {
            this._summarySqftIncluded.textContent = `${formatSqft(sqftIncluded)} sqft`;
        }
        if (this._updateButton) {
            this._updateButton.disabled = subtotal <= 0;
        }

        this._subtotal = subtotal;
    }

    // Write the raw pre-overage Subtotal into the PDP Square Feet input and copy the
    // drawer's overage into the PDP overage <select>, firing the native events the
    // existing calculator already listens for, then close. NOT add-to-cart.
    _onUpdate() {
        const subtotal = measureSqft(this._readSections());
        if (subtotal <= 0) return;

        const sqftTarget = this._sqftTargetId && document.getElementById(this._sqftTargetId);
        const overageTarget =
            this._overageTargetId && document.getElementById(this._overageTargetId);

        if (overageTarget) {
            overageTarget.value = String(this._overagePct());
        }
        if (sqftTarget) {
            sqftTarget.value = String(subtotal);
            sqftTarget.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (overageTarget) {
            overageTarget.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this._drawer) this._drawer.hide();
    }
}

if (!window.customElements.get('sqft-helper')) {
    window.customElements.define('sqft-helper', SqftHelper);
}
